import React, { useEffect, useReducer, useMemo, useCallback } from 'react';
import { apiGet, apiGetLong } from './apiUtils';
import { getCurrentUser } from './authUtils';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './components/ui/card';
import { Button } from './components/ui/button';
import { Alert, AlertDescription } from './components/ui/alert';
import { Spinner } from './components/ui/spinner';
import { FileDown, FileSpreadsheet, AlertTriangle, CheckCircle2, FileText, Package, Calendar, CalendarCheck, IndianRupee } from 'lucide-react';
import DateRangeSelector from './components/DateRangeSelector';
import { useDateRangeSelector } from './hooks/useDateRangeSelector';
import ExcelJS from 'exceljs';

// Constants
const BUSINESS_CONFIG = {
  START_HOUR: 11,
  START_MINUTE: 30,
  TABLE_SPLIT_THRESHOLD: 50,
  IST_OFFSET: 5.5 * 60 * 60 * 1000
};

const FETCH_STATES = {
  IDLE: 'idle',
  LOADING: 'loading',
  SUCCESS: 'success',
  ERROR: 'error'
};

// State reducer for better state management
const initialState = {
  stockData: [],
  summaryData: null,
  incomeData: [],
  expensesData: [],
  paymentsData: null,
  closingStockStatus: null,
  accessorySalesData: null,
  fetchState: FETCH_STATES.IDLE,
  error: null,
  generating: false,
  generatingType: null,
  loadingProgress: null
};

const dataReducer = (state, action) => {
  switch (action.type) {
    case 'FETCH_START':
      return { ...state, fetchState: FETCH_STATES.LOADING, error: null, loadingProgress: null };
    case 'FETCH_SUCCESS':
      return { 
        ...state, 
        fetchState: FETCH_STATES.SUCCESS,
        loadingProgress: null,
        ...action.payload 
      };
    case 'FETCH_ERROR':
      return { 
        ...state, 
        fetchState: FETCH_STATES.ERROR, 
        error: action.payload,
        loadingProgress: null 
      };
    case 'SET_GENERATING':
      return { ...state, generating: action.payload, generatingType: action.payload ? action.generatingType : null };
    case 'UPDATE_DATA':
      return { ...state, ...action.payload };
    case 'SET_STOCK_DATA':
      return { ...state, stockData: action.payload };
    case 'SET_SUMMARY_DATA':
      return { ...state, summaryData: action.payload };
    case 'SET_INCOME_DATA':
      return { ...state, incomeData: action.payload };
    case 'SET_EXPENSES_DATA':
      return { ...state, expensesData: action.payload };
    case 'SET_PAYMENTS_DATA':
      return { ...state, paymentsData: action.payload };
    case 'SET_CLOSING_STOCK_STATUS':
      return { ...state, closingStockStatus: action.payload };
    case 'SET_ACCESSORY_SALES_DATA':
      return { ...state, accessorySalesData: action.payload };
    case 'SET_LOADING_PROGRESS':
      return { ...state, loadingProgress: action.payload };
    default:
      return state;
  }
};

// Error Display Component
const ErrorDisplay = ({ error, onRetry }) => (
  <div className="p-6 max-w-2xl mx-auto">
    <Alert variant="destructive" className="mb-4">
      <AlertTriangle className="h-5 w-5" />
      <AlertDescription className="ml-2">
        <div className="font-semibold mb-2">Error Loading Data</div>
        <div className="text-sm mb-4">
          {error?.message || 'An unexpected error occurred while loading the sale sheet data.'}
        </div>
        <Button onClick={onRetry} variant="outline" size="sm">
          Retry Loading
        </Button>
      </AlertDescription>
    </Alert>
  </div>
);

// Loading Component
const LoadingDisplay = ({ progress }) => (
  <div className="flex flex-col items-center justify-center p-12 gap-4">
    <Spinner className="h-8 w-8 text-primary" />
    <div className="text-center space-y-2">
      <div className="text-base font-semibold text-gray-700">
        Loading sale sheet data...
      </div>
      {progress && (
        <div className="text-sm text-gray-500">
          {progress}
        </div>
      )}
    </div>
  </div>
);

function DownloadSaleSheet() {
  // Use date range selector hook
  const dateSelector = useDateRangeSelector();
  
  // Main state with reducer
  const [state, dispatch] = useReducer(dataReducer, initialState);

  // Memoized user data
  const userData = useMemo(() => getCurrentUser(), []);
  const shopName = useMemo(() => userData.shopName || 'Easy Sheets, Daily', [userData.shopName]);
  
  // Memoized calculations
  const calculations = useMemo(() => {
    const totalSales = state.stockData.reduce((sum, item) => sum + (item.salesValue || 0), 0);
    // Count only products with stock (excluding products with zero stock)
    const totalProducts = state.stockData.filter(item => 
      (item.openingStock || 0) > 0 || 
      (item.receivedStock || 0) > 0 || 
      (item.closingStock || 0) > 0 || 
      (item.sales || 0) > 0
    ).length;
    const showBothFormats = totalProducts > BUSINESS_CONFIG.TABLE_SPLIT_THRESHOLD;
    
    return { totalSales, totalProducts, showBothFormats };
  }, [state.stockData]);

  // Memoized utility functions
  const getDateRange = useCallback(() => {
    const { startDate, endDate } = dateSelector.getDateRange();
    return startDate === endDate ? startDate : `${startDate} to ${endDate}`;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateSelector.reportType, dateSelector.selectedDate, dateSelector.selectedYear, dateSelector.selectedMonth, dateSelector.selectedWeek, dateSelector.customStartDate, dateSelector.customEndDate]);

  // Individual fetch functions
  const fetchStockData = useCallback(async () => {
    try {
      const { startDate, endDate } = dateSelector.getDateRange();
      
      // Validate dates before making API calls
      if (!startDate || !endDate || startDate === 'Invalid Date' || endDate === 'Invalid Date') {
        console.error('Invalid date range:', { startDate, endDate });
        return;
      }
      
      if (startDate === endDate) {
        // Single date logic
        const response = await apiGet(`/api/shop/products?date=${startDate}`);
        const result = await response.json();
        const products = result.products || [];
        
        console.log('📊 Received products from API:', products.length);
        if (products.length > 0) {
          console.log('📋 Sample product from API:', products[0]);
        }
          
        const salesData = products.map((item, index) => {
          const openingStock = item.openingStock || 0;
          // Handle both receivedStock and totalReceivedToday from backend
          const receivedStock = item.receivedStock || item.totalReceivedToday || 0;
          const totalStock = item.totalStock || (openingStock + receivedStock);
          const closingStock = item.closingStock !== null ? item.closingStock : totalStock;
          const sales = Math.max(0, totalStock - closingStock);
          
          console.log(`Product ${item.brandName}: opening=${openingStock}, received=${receivedStock}, total=${totalStock}, closing=${closingStock}, sales=${sales}`);
          
          return {
            serialNo: index + 1,
            brandNumber: item.brandNumber,
            brandName: item.name,
            sizeCode: item.sizeCode,
            packType: item.packType,
            openingStock,
            receivedStock,
            totalStock,
            closingStock,
            sales,
            price: item.finalPrice || 0,
            salesValue: sales * (item.finalPrice || 0),
            productType: item.product_type || item.category || 'IML'
          };
        })
        // Filter out products where O.S, Received, and Total are all zero
        .filter(item => item.openingStock !== 0 || item.receivedStock !== 0 || item.totalStock !== 0);

        dispatch({ type: 'SET_STOCK_DATA', payload: salesData });
      } else {
        // Date range logic: use the /range endpoint which returns products in correct sort order
        // with opening stock from start date, cumulative received stock, and closing stock from end date
        const rangeResponse = await apiGet(`/api/shop/products/range?startDate=${startDate}&endDate=${endDate}`);
        const rangeResult = await rangeResponse.json();
        const rangeProducts = rangeResult.products || [];
        
        console.log('📊 Received products from range API:', rangeProducts.length);
        if (rangeProducts.length > 0) {
          console.log('📋 Sample product from range API:', rangeProducts[0]);
        }
        
        // Map the range products to sales data format
        // The backend already returns them in the correct sort order
        const salesData = rangeProducts.map((item, index) => {
          const openingStock = item.openingStock || 0;
          const receivedStock = item.receivedStock || 0; // Already cumulative from backend
          const totalStock = item.totalStock || (openingStock + receivedStock);
          const closingStock = item.closingStock !== null ? item.closingStock : totalStock;
          const sales = Math.max(0, totalStock - closingStock);
          
          return {
            serialNo: index + 1,
            brandNumber: item.brandNumber,
            brandName: item.name,
            sizeCode: item.sizeCode,
            packType: item.packType,
            openingStock,
            receivedStock,
            totalStock,
            closingStock,
            sales,
            price: item.finalPrice || 0,
            salesValue: sales * (item.finalPrice || 0),
            productType: item.product_type || item.category || 'IML'
          };
        })
        // Filter out products where O.S, Received, and Total are all zero
        .filter(item => item.openingStock !== 0 || item.receivedStock !== 0 || item.totalStock !== 0);

        dispatch({ type: 'SET_STOCK_DATA', payload: salesData });
      }
    } catch (error) {
      console.error('Error fetching stock data:', error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateSelector.reportType, dateSelector.selectedDate, dateSelector.selectedYear, dateSelector.selectedMonth, dateSelector.selectedWeek, dateSelector.customStartDate, dateSelector.customEndDate]);

  const fetchSummaryData = useCallback(async () => {
    try {
      const { startDate, endDate } = dateSelector.getDateRange();
      
      // Validate dates
      if (!startDate || !endDate || startDate === 'Invalid Date' || endDate === 'Invalid Date') {
        console.error('Invalid date range for summary:', { startDate, endDate });
        return;
      }
      
      if (startDate === endDate) {
        // Single date - fetch normally
        const response = await apiGet(`/api/summary?date=${startDate}`);
        const result = await response.json();
        dispatch({ type: 'SET_SUMMARY_DATA', payload: result });
      } else {
        // Date range - get opening balance from start date, closing balance from end date
        const [startResponse, endResponse] = await Promise.all([
          apiGet(`/api/summary?date=${startDate}`),
          apiGet(`/api/summary?date=${endDate}`)
        ]);
        
        const startResult = await startResponse.json();
        const endResult = await endResponse.json();
        
        // Combine: opening from start, closing from end
        const combinedResult = {
          ...endResult,
          openingBalance: startResult.openingBalance || 0,
          counterBalance: endResult.counterBalance || 0
        };
        
        dispatch({ type: 'SET_SUMMARY_DATA', payload: combinedResult });
      }
    } catch (error) {
      console.error('Error fetching summary data:', error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateSelector.reportType, dateSelector.selectedDate, dateSelector.selectedYear, dateSelector.selectedMonth, dateSelector.selectedWeek, dateSelector.customStartDate, dateSelector.customEndDate]);

  const fetchIncomeExpensesData = useCallback(async () => {
    try {
      const { startDate, endDate } = dateSelector.getDateRange();
      
      // Validate dates
      if (!startDate || !endDate || startDate === 'Invalid Date' || endDate === 'Invalid Date') {
        console.error('Invalid date range for income/expenses:', { startDate, endDate });
        return;
      }
      
      if (startDate === endDate) {
        // Single date - fetch normally
        const [incomeResponse, expensesResponse] = await Promise.all([
          apiGet(`/api/income-expenses/income?date=${startDate}`),
          apiGet(`/api/income-expenses/expenses?date=${startDate}`)
        ]);

        if (!incomeResponse.ok || !expensesResponse.ok) {
          console.error('API responses not ok:', { income: incomeResponse.status, expenses: expensesResponse.status });
          dispatch({ type: 'SET_INCOME_DATA', payload: [] });
          dispatch({ type: 'SET_EXPENSES_DATA', payload: [] });
          return;
        }

        const incomeResult = await incomeResponse.json();
        const expensesResult = await expensesResponse.json();

        dispatch({ type: 'SET_INCOME_DATA', payload: Array.isArray(incomeResult) ? incomeResult : [] });
        dispatch({ type: 'SET_EXPENSES_DATA', payload: Array.isArray(expensesResult) ? expensesResult : [] });
      } else {
        // Date range - use efficient backend endpoint for range queries
        console.log(`📅 Fetching income/expenses range: ${startDate} to ${endDate}`);
        
        const [incomeResponse, expensesResponse] = await Promise.all([
          apiGet(`/api/income-expenses/income-range?startDate=${startDate}&endDate=${endDate}`),
          apiGet(`/api/income-expenses/expenses-range?startDate=${startDate}&endDate=${endDate}`)
        ]);

        if (!incomeResponse.ok || !expensesResponse.ok) {
          console.error('API responses not ok:', { income: incomeResponse.status, expenses: expensesResponse.status });
          dispatch({ type: 'SET_INCOME_DATA', payload: [] });
          dispatch({ type: 'SET_EXPENSES_DATA', payload: [] });
          return;
        }

        const incomeResult = await incomeResponse.json();
        const expensesResult = await expensesResponse.json();

        console.log(`✅ Fetched ${incomeResult.length} income records and ${expensesResult.length} expense records`);
        dispatch({ type: 'SET_INCOME_DATA', payload: Array.isArray(incomeResult) ? incomeResult : [] });
        dispatch({ type: 'SET_EXPENSES_DATA', payload: Array.isArray(expensesResult) ? expensesResult : [] });
      }
    } catch (error) {
      console.error('Error fetching income/expenses data:', error);
      dispatch({ type: 'SET_INCOME_DATA', payload: [] });
      dispatch({ type: 'SET_EXPENSES_DATA', payload: [] });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateSelector.reportType, dateSelector.selectedDate, dateSelector.selectedYear, dateSelector.selectedMonth, dateSelector.selectedWeek, dateSelector.customStartDate, dateSelector.customEndDate]);

  const fetchPaymentsData = useCallback(async () => {
    try {
      const { startDate, endDate } = dateSelector.getDateRange();
      
      // Validate dates
      if (!startDate || !endDate || startDate === 'Invalid Date' || endDate === 'Invalid Date') {
        console.error('Invalid date range for payments:', { startDate, endDate });
        return;
      }
      
      if (startDate === endDate) {
        // Single date - fetch normally
        const response = await apiGet(`/api/payments?date=${startDate}`);
        const result = await response.json();
        dispatch({ type: 'SET_PAYMENTS_DATA', payload: result.payment || {} });
      } else {
        // Date range - use efficient backend endpoint for aggregated payments
        console.log(`📅 Fetching aggregated payments: ${startDate} to ${endDate}`);
        
        const response = await apiGet(`/api/payments-range?startDate=${startDate}&endDate=${endDate}`);
        
        if (!response.ok) {
          console.error('Payments range API returned error:', response.status);
          dispatch({ type: 'SET_PAYMENTS_DATA', payload: { cash_amount: 0, upi_amount: 0, card_amount: 0 }});
          return;
        }
        
        const result = await response.json();
        console.log(`✅ Aggregated payments: Cash=${result.cash_amount}, UPI=${result.upi_amount}, Card=${result.card_amount}`);
        
        dispatch({ type: 'SET_PAYMENTS_DATA', payload: result });
      }
    } catch (error) {
      console.error('Error fetching payments data:', error);
      dispatch({ type: 'SET_PAYMENTS_DATA', payload: { cash_amount: 0, upi_amount: 0, card_amount: 0 }});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateSelector.reportType, dateSelector.selectedDate, dateSelector.selectedYear, dateSelector.selectedMonth, dateSelector.selectedWeek, dateSelector.customStartDate, dateSelector.customEndDate]);

  const fetchClosingStockStatus = useCallback(async () => {
    try {
      // Always check closing stock status for the end date
      const { endDate } = dateSelector.getDateRange();
      const targetDate = endDate;
      
      // Validate date
      if (!targetDate || targetDate === 'Invalid Date') {
        console.error('Invalid target date for closing stock:', targetDate);
        return;
      }
      
      const response = await apiGet(`/api/shop/products?date=${targetDate}`);
      const result = await response.json();
      dispatch({ type: 'SET_CLOSING_STOCK_STATUS', payload: result.closingStockStatus });
    } catch (error) {
      console.error('Error fetching closing stock status:', error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateSelector.reportType, dateSelector.selectedDate, dateSelector.selectedYear, dateSelector.selectedMonth, dateSelector.selectedWeek, dateSelector.customStartDate, dateSelector.customEndDate]);

  const fetchAccessorySalesData = useCallback(async () => {
    try {
      const { startDate, endDate } = dateSelector.getDateRange();
      
      // Validate dates
      if (!startDate || !endDate || startDate === 'Invalid Date' || endDate === 'Invalid Date') {
        console.error('Invalid date range for accessories:', { startDate, endDate });
        return;
      }
      
      if (startDate === endDate) {
        // Single date - fetch daily accessories data
        const response = await apiGet(`/api/accessories/daily?date=${startDate}`);
        if (!response.ok) {
          console.error('Accessories API returned error:', response.status);
          dispatch({ type: 'SET_ACCESSORY_SALES_DATA', payload: { totalSales: 0, items: [] } });
          return;
        }
        
        const data = await response.json();
        // API returns { accessories: [...], date, totalItems, ... }
        const accessories = data.accessories || data || [];
        
        // Calculate total sales value from accessories
        const totalSales = accessories.reduce((sum, item) => {
          // If sale_value is already calculated, use it
          if (item.sale_value) {
            return sum + (parseFloat(item.sale_value) || 0);
          }
          // Otherwise calculate: sales = total_stock - closing_stock, then multiply by price
          const openingStock = item.opening_stock || 0;
          const receivedStock = item.received_stock || 0;
          const totalStock = openingStock + receivedStock;
          const closingStock = item.closing_stock !== null ? item.closing_stock : totalStock;
          const sales = Math.max(0, totalStock - closingStock);
          const price = parseFloat(item.price_per_unit) || parseFloat(item.mrp) || 0;
          return sum + (sales * price);
        }, 0);
        
        console.log(`✅ Accessories: ${accessories.length} items, Total Sales: ₹${totalSales}`);
        dispatch({ type: 'SET_ACCESSORY_SALES_DATA', payload: { totalSales, items: accessories } });
      } else {
        // Date range - use efficient backend endpoint for range queries
        console.log(`📅 Fetching accessories range: ${startDate} to ${endDate}`);
        
        const response = await apiGet(`/api/accessories/daily-range?startDate=${startDate}&endDate=${endDate}`);
        
        if (!response.ok) {
          console.error('Accessories range API returned error:', response.status);
          dispatch({ type: 'SET_ACCESSORY_SALES_DATA', payload: { totalSales: 0, items: [] } });
          return;
        }
        
        const data = await response.json();
        const { accessoriesByDate = {}, totalSales = 0 } = data;
        
        // Flatten the by-date data into a single items array
        const allItems = [];
        for (const [date, items] of Object.entries(accessoriesByDate)) {
          for (const item of items) {
            allItems.push({ ...item, date });
          }
        }
        
        console.log(`✅ Accessories range: Total Sales: ₹${totalSales}`);
        dispatch({ type: 'SET_ACCESSORY_SALES_DATA', payload: { totalSales, items: allItems } });
      }
    } catch (error) {
      console.error('Error fetching accessory sales data:', error);
      dispatch({ type: 'SET_ACCESSORY_SALES_DATA', payload: { totalSales: 0, items: [] } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateSelector.reportType, dateSelector.selectedDate, dateSelector.selectedYear, dateSelector.selectedMonth, dateSelector.selectedWeek, dateSelector.customStartDate, dateSelector.customEndDate]);

  // Main fetch function that calls all individual fetch functions
  const fetchAllData = useCallback(async () => {
    dispatch({ type: 'FETCH_START' });
    try {
      console.log('🔄 Starting to fetch all data...');
      
      const totalSteps = 6;
      let completedSteps = 0;
      
      const updateProgress = (step) => {
        completedSteps++;
        dispatch({ type: 'SET_LOADING_PROGRESS', payload: `${step} (${completedSteps}/${totalSteps})` });
      };
      
      dispatch({ type: 'SET_LOADING_PROGRESS', payload: 'Fetching stock data... (1/6)' });
      await fetchStockData();
      updateProgress('Stock data loaded');
      
      dispatch({ type: 'SET_LOADING_PROGRESS', payload: 'Fetching summary... (2/6)' });
      await fetchSummaryData();
      updateProgress('Summary loaded');
      
      dispatch({ type: 'SET_LOADING_PROGRESS', payload: 'Fetching income & expenses... (3/6)' });
      await fetchIncomeExpensesData();
      updateProgress('Income & expenses loaded');
      
      dispatch({ type: 'SET_LOADING_PROGRESS', payload: 'Fetching payments... (4/6)' });
      await fetchPaymentsData();
      updateProgress('Payments loaded');
      
      dispatch({ type: 'SET_LOADING_PROGRESS', payload: 'Fetching accessories sales... (5/6)' });
      await fetchAccessorySalesData();
      updateProgress('Accessories loaded');
      
      dispatch({ type: 'SET_LOADING_PROGRESS', payload: 'Verifying closing stock... (6/6)' });
      await fetchClosingStockStatus();
      updateProgress('Closing stock verified');
      
      console.log('✅ All data fetched successfully');
      dispatch({ type: 'FETCH_SUCCESS', payload: {} });
    } catch (error) {
      console.error('❌ Error fetching data:', error);
      dispatch({ type: 'FETCH_ERROR', payload: error });
    }
  }, [fetchStockData, fetchSummaryData, fetchIncomeExpensesData, fetchPaymentsData, fetchAccessorySalesData, fetchClosingStockStatus]);

  // Effect to fetch data when dates change
  useEffect(() => {
    if (dateSelector.selectedDate) {
      fetchAllData();
    }
  }, [fetchAllData, dateSelector.reportType, dateSelector.selectedDate, dateSelector.selectedYear, dateSelector.selectedMonth, dateSelector.selectedWeek, dateSelector.customStartDate, dateSelector.customEndDate]);


  const calculateTotalSales = useCallback(() => {
    return state.stockData.reduce((total, item) => total + (item.salesValue || 0), 0);
  }, [state.stockData]);


  const generateSingleTablePDF = useCallback(() => {
    console.log('generateSingleTablePDF called');
    console.log('state.stockData:', state.stockData);
    console.log('shopName:', shopName);
    
    dispatch({ type: 'SET_GENERATING', payload: true, generatingType: 'pdf' });
    
    try {
      // eslint-disable-next-line no-use-before-define
      const pdfContent = createPDFContent(false);
      console.log('PDF content generated, length:', pdfContent.length);
      
      if (!pdfContent || pdfContent.length === 0) {
        throw new Error('Failed to generate PDF content');
      }
      
      openPrintWindow(pdfContent);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to generate PDF. Please try again or contact support if the issue persists.');
      dispatch({ type: 'SET_GENERATING', payload: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, shopName, dateSelector.reportType, dateSelector.selectedDate, dateSelector.customStartDate, dateSelector.customEndDate, calculateTotalSales]);

  const generateTwoTablePDF = useCallback(() => {
    console.log('generateTwoTablePDF called');
    dispatch({ type: 'SET_GENERATING', payload: true, generatingType: 'pdf' });
    
    try {
      // Detect if mobile/tablet for proper scaling
      const isMobileOrTablet = window.innerWidth <= 1024;
      // eslint-disable-next-line no-use-before-define
      const pdfContent = createPDFContent(true, isMobileOrTablet);
      
      if (!pdfContent || pdfContent.length === 0) {
        throw new Error('Failed to generate PDF content');
      }
      
      openPrintWindow(pdfContent);
    } catch (error) {
      console.error('Error generating two-table PDF:', error);
      alert('Failed to generate PDF. Please try again or contact support if the issue persists.');
      dispatch({ type: 'SET_GENERATING', payload: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, shopName, dateSelector.reportType, dateSelector.selectedDate, dateSelector.customStartDate, dateSelector.customEndDate, calculateTotalSales]);

  const generateExcelFile = useCallback(async () => {
    dispatch({ type: 'SET_GENERATING', payload: true, generatingType: 'excel' });

    try {
      const { startDate, endDate } = dateSelector.getDateRange();
      const isSingleDate = startDate === endDate;

      const thinBorder = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
      const thickTopBottom = { top: { style: 'medium' }, left: { style: 'thin' }, bottom: { style: 'medium' }, right: { style: 'thin' } };
      const headerFill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE2E8F0' } };
      const greenFill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD1FAE5' } };
      const tableFont = { size: 12 };
      const headerFont = { bold: true, size: 12 };
      const redBoldFont = { bold: true, color: { argb: 'FFFF0000' }, size: 12 };
      const greenBoldFont = { bold: true, size: 12, color: { argb: 'FF065F46' } };
      const centerAlign = { horizontal: 'center', vertical: 'middle' };
      const leftAlign = { horizontal: 'left', vertical: 'middle' };
      const rightAlign = { horizontal: 'right', vertical: 'middle' };
      const sectionTitleFont = { bold: true, size: 14 };
      const indianFmt = '#,##,##0';

      const applyBorders = (ws, r1, r2, c1, c2) => {
        for (let r = r1; r <= r2; r++)
          for (let c = c1; c <= c2; c++)
            ws.getRow(r).getCell(c).border = thinBorder;
      };

      // ---- Helper: fetch all data for a single date (single API call) ----
      const fetchDayData = async (date) => {
        const res = await apiGetLong(`/api/shop/daily-complete?date=${date}`, 30000);
        const data = await res.json();

        const products = data.products || [];
        const stockData = products.map((item, index) => {
          const os = item.openingStock || 0;
          const rec = item.receivedStock || item.totalReceivedToday || 0;
          const ttl = item.totalStock || (os + rec);
          const cs = item.closingStock !== null ? item.closingStock : ttl;
          const sales = Math.max(0, ttl - cs);
          return {
            serialNo: index + 1, brandNumber: item.brandNumber, brandName: item.name,
            sizeCode: item.sizeCode, packType: item.packType,
            openingStock: os, receivedStock: rec, totalStock: ttl, closingStock: cs,
            sales, price: item.finalPrice || 0, salesValue: sales * (item.finalPrice || 0)
          };
        }).filter(i => i.openingStock !== 0 || i.receivedStock !== 0 || i.totalStock !== 0);

        const summaryData = data.summary || {};
        const incomeData = Array.isArray(data.income) ? data.income : [];
        const expensesData = Array.isArray(data.expenses) ? data.expenses : [];
        const paymentsData = data.payment || {};

        const accessories = data.accessories?.accessories || [];
        const totalSales = accessories.reduce((sum, item) => {
          if (item.sale_value) return sum + (parseFloat(item.sale_value) || 0);
          const aOs = item.opening_stock || 0;
          const aRec = item.received_stock || 0;
          const aTtl = aOs + aRec;
          const aCs = item.closing_stock !== null ? item.closing_stock : aTtl;
          const aSales = Math.max(0, aTtl - aCs);
          return sum + (aSales * (parseFloat(item.price_per_unit) || parseFloat(item.mrp) || 0));
        }, 0);
        const accessorySalesData = { totalSales, items: accessories };

        return { stockData, summaryData, incomeData, expensesData, paymentsData, accessorySalesData };
      };

      // ---- Helper: build a complete sale sheet on a worksheet ----
      const buildSheet = (ws, data, sheetDateLabel) => {
        ws.columns = [
          { width: 5 }, { width: 35 }, { width: 16 }, { width: 8 }, { width: 8 },
          { width: 8 }, { width: 8 }, { width: 8 }, { width: 10 }, { width: 14 }
        ];

        // Row 1: SALE SHEET
        ws.mergeCells('A1:J1');
        const tc = ws.getCell('A1');
        tc.value = 'SALE SHEET'; tc.font = { bold: true, size: 18 }; tc.alignment = { horizontal: 'center', vertical: 'middle' };
        ws.getRow(1).height = 28;

        // Row 2: Shop | Date
        ws.mergeCells('A2:I2');
        const sc = ws.getCell('A2');
        sc.value = shopName; sc.font = { bold: true, size: 14 }; sc.alignment = leftAlign;
        const dc = ws.getCell('J2');
        dc.value = sheetDateLabel; dc.font = { bold: true, size: 14 }; dc.alignment = rightAlign;
        ws.getRow(2).height = 22;

        // Row 3: Headers
        ['SNO', 'NAME', 'Size Code', 'O.S', 'REC', 'TTL', 'C.S', 'SALE', 'PRICE', 'AMOUNT'].forEach((h, i) => {
          const cell = ws.getRow(3).getCell(i + 1);
          cell.value = h; cell.font = headerFont; cell.fill = headerFill; cell.alignment = centerAlign; cell.border = thinBorder;
        });
        ws.getRow(3).height = 20;

        // Size code groups
        const groups = {};
        data.stockData.forEach(item => {
          const k = `${item.brandNumber}_${item.sizeCode}`;
          if (!groups[k]) groups[k] = new Set();
          if (item.packType) groups[k].add(item.packType);
        });

        // Data rows
        const ds = 4;
        data.stockData.forEach((item, idx) => {
          const r = ds + idx;
          const row = ws.getRow(r);
          let sc2 = item.sizeCode;
          const k = `${item.brandNumber}_${item.sizeCode}`;
          if (groups[k] && groups[k].size > 1 && item.packType) sc2 = `${item.sizeCode}(${item.packType === 'P' ? 'P' : 'G'})`;

          row.getCell(1).value = item.serialNo;
          row.getCell(2).value = `${item.brandName}(${item.brandNumber})`;
          row.getCell(3).value = sc2;
          row.getCell(4).value = item.openingStock;
          row.getCell(5).value = item.receivedStock;
          row.getCell(6).value = { formula: `D${r}+E${r}` };
          row.getCell(7).value = item.closingStock;
          row.getCell(8).value = { formula: `F${r}-G${r}` };
          row.getCell(9).value = Math.round(item.price);
          row.getCell(10).value = { formula: `H${r}*I${r}` };
          for (let c = 1; c <= 10; c++) { row.getCell(c).font = tableFont; row.getCell(c).alignment = c === 2 ? leftAlign : centerAlign; }
          row.getCell(10).numFmt = indianFmt;
        });

        // Merge brand name cells for consecutive rows of the same brand
        if (data.stockData.length > 1) {
          let groupStart = 0;
          for (let i = 1; i <= data.stockData.length; i++) {
            const sameBrand = i < data.stockData.length && data.stockData[i].brandNumber === data.stockData[groupStart].brandNumber;
            if (!sameBrand) {
              if (i - groupStart > 1) {
                const startRow = ds + groupStart;
                const endRow = ds + i - 1;
                ws.mergeCells(`B${startRow}:B${endRow}`);
                const merged = ws.getCell(`B${startRow}`);
                merged.alignment = { horizontal: 'left', vertical: 'middle' };
                merged.font = tableFont;
              }
              groupStart = i;
            }
          }
        }

        const hasProducts = data.stockData.length > 0;
        const de = hasProducts ? ds + data.stockData.length - 1 : ds;
        const ptr = de + (hasProducts ? 1 : 0);

        // TOTAL BOTTLES - red
        const tr = ws.getRow(hasProducts ? ptr : ds);
        tr.getCell(2).value = 'TOTAL BOTTLES';
        if (hasProducts) {
          [4,5,6,7,8].forEach(c => { tr.getCell(c).value = { formula: `SUM(${String.fromCharCode(64+c)}${ds}:${String.fromCharCode(64+c)}${de})` }; });
          tr.getCell(10).value = { formula: `SUM(J${ds}:J${de})` };
        } else {
          [4,5,6,7,8,10].forEach(c => { tr.getCell(c).value = 0; });
        }
        for (let c = 1; c <= 10; c++) { tr.getCell(c).font = redBoldFont; tr.getCell(c).alignment = c === 2 ? leftAlign : centerAlign; tr.getCell(c).border = thickTopBottom; }
        tr.getCell(10).numFmt = indianFmt;
        if (hasProducts) applyBorders(ws, ds, de, 1, 10);

        let cr = ptr + 1;

        // Accessories
        const accItems = data.accessorySalesData?.items || [];
        if (accItems.length > 0) {
          cr++;
          ws.mergeCells(`A${cr}:J${cr}`);
          const at = ws.getCell(`A${cr}`);
          at.value = 'ACCESSORIES SALE:'; at.font = { bold: true, size: 14, color: { argb: 'FF059669' } }; at.alignment = leftAlign;
          cr++;
          ['SNO', 'NAME', 'SIZE', 'O.S', 'REC', 'TTL', 'C.S', 'SALE', 'PRICE', 'AMOUNT'].forEach((h, i) => {
            const cell = ws.getRow(cr).getCell(i + 1);
            cell.value = h; cell.font = headerFont; cell.fill = greenFill; cell.alignment = centerAlign; cell.border = thinBorder;
          });
          cr++;
          const ads = cr;
          accItems.forEach((item, idx) => {
            const r = cr; const row = ws.getRow(r);
            const os = item.opening_stock || 0; const rec = item.received_stock || 0;
            const cs = item.closing_stock !== null ? item.closing_stock : (os + rec);
            const price = parseFloat(item.price_per_unit) || parseFloat(item.mrp) || 0;
            row.getCell(1).value = idx + 1; row.getCell(2).value = item.name; row.getCell(3).value = item.size || '-';
            row.getCell(4).value = os; row.getCell(5).value = rec; row.getCell(6).value = { formula: `D${r}+E${r}` };
            row.getCell(7).value = cs; row.getCell(8).value = { formula: `F${r}-G${r}` };
            row.getCell(9).value = Math.round(price); row.getCell(10).value = { formula: `H${r}*I${r}` };
            for (let c = 1; c <= 10; c++) { row.getCell(c).font = tableFont; row.getCell(c).alignment = c === 2 ? leftAlign : centerAlign; }
            row.getCell(10).numFmt = indianFmt;
            cr++;
          });
          const ade = cr - 1;
          const atr = ws.getRow(cr);
          atr.getCell(2).value = 'TOTAL ACCESSORIES';
          [4,5,6,7,8].forEach(c => { atr.getCell(c).value = { formula: `SUM(${String.fromCharCode(64+c)}${ads}:${String.fromCharCode(64+c)}${ade})` }; });
          atr.getCell(10).value = { formula: `SUM(J${ads}:J${ade})` };
          for (let c = 1; c <= 10; c++) { atr.getCell(c).font = greenBoldFont; atr.getCell(c).alignment = c === 2 ? leftAlign : centerAlign; atr.getCell(c).border = thickTopBottom; }
          atr.getCell(10).numFmt = indianFmt;
          applyBorders(ws, ads, ade, 1, 10);
          cr++;
        }

        // Summary
        const incArr = data.incomeData;
        const expArr = data.expensesData;
        const accSales = data.accessorySalesData?.totalSales || 0;
        const combInc = accSales > 0 ? [{ source: 'Accessories Sale', amount: accSales, description: 'Cool drinks, water bottles, Sodas etc' }, ...incArr] : incArr;
        const cash = data.paymentsData?.cash_amount || 0;
        const upi = data.paymentsData?.upi_amount || 0;
        const card = data.paymentsData?.card_amount || 0;
        const ob = data.summaryData?.openingBalance || 0;

        cr++;
        ws.mergeCells(`A${cr}:J${cr}`);
        const st = ws.getCell(`A${cr}`); st.value = 'SUMMARY:'; st.font = sectionTitleFont; st.alignment = leftAlign;
        cr++;
        ['S.No', 'PARTICULARS', 'AMOUNT'].forEach((h, i) => {
          const cell = ws.getRow(cr).getCell(i + 1);
          cell.value = h; cell.font = headerFont; cell.fill = headerFill; cell.alignment = centerAlign; cell.border = thinBorder;
        });
        cr++;

        const sds = cr;
        const sR = (o) => sds + o;
        const sLabels = [[1,'OPENING STOCK VALUE'],[2,'RECEIVED STOCK VALUE'],[3,'CLOSING STOCK VALUE'],[4,'TOTAL SALE'],[5,'OTHER INCOME'],[6,'OPENING COUNTER BALANCE'],[7,'CASH'],[8,'CARD'],[9,'UPI'],[10,'EXPENSES'],[11,'CLOSING COUNTER BALANCE']];
        sLabels.forEach((rd, idx) => {
          const r = sds + idx; const row = ws.getRow(r);
          row.getCell(1).value = rd[0]; row.getCell(2).value = rd[1];
          row.getCell(1).alignment = centerAlign; row.getCell(2).alignment = leftAlign; row.getCell(3).alignment = rightAlign;
          for (let c = 1; c <= 3; c++) { row.getCell(c).font = tableFont; row.getCell(c).border = thinBorder; }
        });

        ws.getRow(sR(0)).getCell(3).value = hasProducts ? { formula: `SUMPRODUCT(D${ds}:D${de},I${ds}:I${de})` } : 0;
        ws.getRow(sR(1)).getCell(3).value = hasProducts ? { formula: `SUMPRODUCT(E${ds}:E${de},I${ds}:I${de})` } : 0;
        ws.getRow(sR(2)).getCell(3).value = hasProducts ? { formula: `SUMPRODUCT(G${ds}:G${de},I${ds}:I${de})` } : 0;
        ws.getRow(sR(3)).getCell(3).value = { formula: `C${sR(0)}+C${sR(1)}-C${sR(2)}` };
        ws.getRow(sR(5)).getCell(3).value = ob;
        ws.getRow(sR(6)).getCell(3).value = cash;
        ws.getRow(sR(7)).getCell(3).value = card;
        ws.getRow(sR(8)).getCell(3).value = upi;
        ws.getRow(sR(10)).getCell(3).value = { formula: `C${sR(3)}+C${sR(4)}+C${sR(5)}-C${sR(6)}-C${sR(8)}-C${sR(7)}-C${sR(9)}` };

        // Apply Indian number format AFTER all values are set
        for (let i = 0; i < sLabels.length; i++) {
          ws.getRow(sR(i)).getCell(3).numFmt = indianFmt;
        }

        cr = sds + sLabels.length;

        // Other Income (always show description column for daily sheets)
        cr++;
        ws.mergeCells(`A${cr}:J${cr}`);
        const it = ws.getCell(`A${cr}`); it.value = 'OTHER INCOME:'; it.font = sectionTitleFont; it.alignment = leftAlign;
        cr++;
        const incHdrs = ['S.No', 'SOURCE', 'DESCRIPTION', 'AMOUNT'];
        incHdrs.forEach((h, i) => { const cell = ws.getRow(cr).getCell(i+1); cell.value = h; cell.font = headerFont; cell.fill = headerFill; cell.alignment = centerAlign; cell.border = thinBorder; });
        cr++;

        const ids = cr;
        combInc.forEach((item, idx) => {
          const row = ws.getRow(cr);
          row.getCell(1).value = idx + 1; row.getCell(2).value = item.source || 'N/A';
          row.getCell(3).value = item.description || '-'; row.getCell(4).value = parseFloat(item.amount) || 0;
          for (let c = 1; c <= 4; c++) { row.getCell(c).font = tableFont; row.getCell(c).border = thinBorder; }
          row.getCell(4).numFmt = indianFmt;
          cr++;
        });
        const ide = cr - 1;
        const itr2 = ws.getRow(cr);
        itr2.getCell(2).value = 'TOTAL';
        itr2.getCell(4).value = combInc.length > 0 ? { formula: `SUM(D${ids}:D${ide})` } : 0;
        itr2.getCell(4).numFmt = indianFmt;
        for (let c = 1; c <= 4; c++) { itr2.getCell(c).font = { bold: true, size: 12 }; itr2.getCell(c).border = thickTopBottom; }
        if (combInc.length > 0) applyBorders(ws, ids, ide, 1, 4);

        ws.getRow(sR(4)).getCell(3).value = { formula: `D${cr}` };
        cr++;

        // Expenses
        cr++;
        ws.mergeCells(`A${cr}:J${cr}`);
        const et = ws.getCell(`A${cr}`); et.value = 'EXPENSES:'; et.font = sectionTitleFont; et.alignment = leftAlign;
        cr++;
        const expHdrs = ['S.No', 'CATEGORY', 'DESCRIPTION', 'AMOUNT'];
        expHdrs.forEach((h, i) => { const cell = ws.getRow(cr).getCell(i+1); cell.value = h; cell.font = headerFont; cell.fill = headerFill; cell.alignment = centerAlign; cell.border = thinBorder; });
        cr++;

        const eds = cr;
        expArr.forEach((item, idx) => {
          const row = ws.getRow(cr);
          row.getCell(1).value = idx + 1; row.getCell(2).value = item.category || 'N/A';
          row.getCell(3).value = item.description || '-'; row.getCell(4).value = parseFloat(item.amount) || 0;
          for (let c = 1; c <= 4; c++) { row.getCell(c).font = tableFont; row.getCell(c).border = thinBorder; }
          row.getCell(4).numFmt = indianFmt;
          cr++;
        });
        const ede = cr - 1;
        const etr2 = ws.getRow(cr);
        etr2.getCell(2).value = 'TOTAL';
        etr2.getCell(4).value = expArr.length > 0 ? { formula: `SUM(D${eds}:D${ede})` } : 0;
        etr2.getCell(4).numFmt = indianFmt;
        for (let c = 1; c <= 4; c++) { etr2.getCell(c).font = { bold: true, size: 12 }; etr2.getCell(c).border = thickTopBottom; }
        if (expArr.length > 0) applyBorders(ws, eds, ede, 1, 4);

        ws.getRow(sR(9)).getCell(3).value = { formula: `D${cr}` };
      };

      // ---- Helper: generate all dates between start and end ----
      const getDateRange = (start, end) => {
        const dates = [];
        const cur = new Date(start + 'T00:00:00');
        const last = new Date(end + 'T00:00:00');
        while (cur <= last) {
          const y = cur.getFullYear();
          const m = String(cur.getMonth() + 1).padStart(2, '0');
          const d = String(cur.getDate()).padStart(2, '0');
          dates.push(`${y}-${m}-${d}`);
          cur.setDate(cur.getDate() + 1);
        }
        return dates;
      };

      const formatTabDate = (dateStr) => {
        const d = new Date(dateStr + 'T00:00:00');
        return `${String(d.getDate()).padStart(2, '0')}-${String(d.getMonth() + 1).padStart(2, '0')}-${d.getFullYear()}`;
      };

      const formatShortDate = (dateStr) => {
        const d = new Date(dateStr + 'T00:00:00');
        return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' });
      };

      const wb = new ExcelJS.Workbook();

      if (isSingleDate) {
        const ws = wb.addWorksheet('Sale Sheet');
        buildSheet(ws, {
          stockData: state.stockData,
          summaryData: state.summaryData,
          incomeData: Array.isArray(state.incomeData) ? state.incomeData : [],
          expensesData: Array.isArray(state.expensesData) ? state.expensesData : [],
          paymentsData: state.paymentsData,
          accessorySalesData: state.accessorySalesData
        }, formatShortDate(startDate));
      } else {
        const dates = getDateRange(startDate, endDate);
        console.log(`Excel generation: ${dates.length} dates from ${startDate} to ${endDate}`);
        let sheetsCreated = 0;
        for (let i = 0; i < dates.length; i++) {
          dispatch({ type: 'SET_LOADING_PROGRESS', payload: `Generating sheet ${i + 1} of ${dates.length}...` });
          const tabName = formatTabDate(dates[i]);
          const ws = wb.addWorksheet(tabName);
          try {
            const dayData = await fetchDayData(dates[i]);
            buildSheet(ws, dayData, formatShortDate(dates[i]));
          } catch (dayError) {
            console.error(`Error generating sheet for ${dates[i]}:`, dayError);
            ws.getCell('A1').value = `Error loading data for ${dates[i]}`;
            ws.getCell('A1').font = { bold: true, color: { argb: 'FFFF0000' }, size: 14 };
          }
          sheetsCreated++;
        }
        console.log(`Excel generation complete: ${sheetsCreated} sheets created, workbook has ${wb.worksheets.length} worksheets`);
        dispatch({ type: 'SET_LOADING_PROGRESS', payload: null });
      }

      // Download
      const buffer = await wb.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const dateLabel = isSingleDate ? formatShortDate(startDate) : `${formatShortDate(startDate)}_to_${formatShortDate(endDate)}`;
      const safeDateLabel = dateLabel.replace(/\s+/g, '_').replace(/\//g, '-');
      a.download = `SaleSheet_${shopName.replace(/[^a-zA-Z0-9]/g, '_')}_${safeDateLabel}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error generating Excel:', error);
      alert('Failed to generate Excel file. Please try again.');
    } finally {
      dispatch({ type: 'SET_GENERATING', payload: false });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, shopName, dateSelector.reportType, dateSelector.selectedDate, dateSelector.customStartDate, dateSelector.customEndDate, calculateTotalSales]);

  const openPrintWindow = (content) => {
    try {
      // Open in new tab/window - called synchronously to avoid popup blockers
      const printWindow = window.open('', '_blank');
      
      if (!printWindow) {
        // Popup was blocked
        alert('Please allow popups for this site to view the sale sheet in a new tab.');
        dispatch({ type: 'SET_GENERATING', payload: false });
        return;
      }
      
      // Write content to new window
      printWindow.document.open();
      printWindow.document.write(content);
      printWindow.document.close();
      
      // Reset generating state after window opens
      setTimeout(() => {
        dispatch({ type: 'SET_GENERATING', payload: false });
      }, 500);
      
    } catch (error) {
      console.error('Error opening print window:', error);
      alert('Failed to open sale sheet. Please try again.');
      dispatch({ type: 'SET_GENERATING', payload: false });
    }
  };

  const createPDFContent = (twoTableFormat = false, isMobileOrTablet = false) => {
    console.log('createPDFContent called with twoTableFormat:', twoTableFormat, 'isMobileOrTablet:', isMobileOrTablet);
    const { startDate, endDate } = dateSelector.getDateRange();
    console.log('Available variables:', {
      'state.stockData length': state.stockData?.length,
      'shopName': shopName,
      'reportType': dateSelector.reportType,
      'startDate': startDate,
      'endDate': endDate
    });
    
    const formatDate = (dateStr) => {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-GB', { 
        day: '2-digit', 
        month: 'short', 
        year: '2-digit' 
      });
    };

    const formatCurrency = (amount) => {
      return Math.round(amount).toLocaleString('en-IN');
    };

    const totalSales = calculateTotalSales();
    console.log('PDF calculations:', {
      totalSales,
      incomeDataLength: state.incomeData?.length,
      expensesDataLength: state.expensesData?.length,
      paymentsData: state.paymentsData,
      summaryData: state.summaryData
    });
    
    // Debug logging to check data types
    console.log('Debug - state.incomeData type:', typeof state.incomeData);
    console.log('Debug - state.incomeData:', state.incomeData);
    console.log('Debug - state.incomeData is array:', Array.isArray(state.incomeData));
    
    // Ensure we have arrays before using reduce
    const incomeArray = Array.isArray(state.incomeData) ? state.incomeData : [];
    const expensesArray = Array.isArray(state.expensesData) ? state.expensesData : [];
    
    // Get accessory sales and add to income
    const accessorySales = state.accessorySalesData?.totalSales || 0;
    
    // Create combined income array with accessories sale as first item (if > 0)
    const combinedIncomeArray = accessorySales > 0 
      ? [{ source: 'Accessories Sale', amount: accessorySales, description: 'Cool drinks, water bottles, Sodas etc' }, ...incomeArray]
      : incomeArray;
    
    console.log('Debug - incomeArray length:', incomeArray.length);
    console.log('Debug - expensesArray length:', expensesArray.length);
    console.log('Debug - accessorySales:', accessorySales);
    
    const totalIncome = combinedIncomeArray.reduce((sum, item) => {
      if (!item || typeof item !== 'object') {
        console.warn('Invalid income item:', item);
        return sum;
      }
      return sum + (parseFloat(item.amount) || 0);
    }, 0);
    
    const totalExpenses = expensesArray.reduce((sum, item) => {
      if (!item || typeof item !== 'object') {
        console.warn('Invalid expense item:', item);
        return sum;
      }
      return sum + (parseFloat(item.amount) || 0);
    }, 0);
    
    const cash = state.paymentsData?.cash_amount || 0;
    const upi = state.paymentsData?.upi_amount || 0;
    const card = state.paymentsData?.card_amount || 0;
    
    const openingBalance = state.summaryData?.openingBalance || 0;
    const closingBalance = state.summaryData?.counterBalance || 0;
    
    // Calculate opening stock value: sum of (opening stock * price)
    const openingStockValue = state.stockData.reduce((total, item) => {
      return total + ((item.openingStock || 0) * (item.price || 0));
    }, 0);
    
    // Calculate closing stock value: sum of (closing stock * price)
    const closingStockValue = state.stockData.reduce((total, item) => {
      return total + ((item.closingStock || 0) * (item.price || 0));
    }, 0);

    // Calculate received stock value: sum of (received stock * price)
    const receivedStockValue = state.stockData.reduce((total, item) => {
      return total + ((item.receivedStock || 0) * (item.price || 0));
    }, 0);

    // Generate table rows
    const generateTableRows = (data) => {
      // First, identify brand+size combinations that have multiple products with different pack types
      const brandSizeGroups = {};
      data.forEach(item => {
        const key = `${item.brandNumber}_${item.sizeCode}`;
        if (!brandSizeGroups[key]) {
          brandSizeGroups[key] = new Set();
        }
        if (item.packType) {
          brandSizeGroups[key].add(item.packType);
        }
      });
      
      return data.map(item => {
        // Only add pack type indicator if this brand+size combination has multiple different pack types
        let displaySizeCode = item.sizeCode;
        const key = `${item.brandNumber}_${item.sizeCode}`;
        if (brandSizeGroups[key] && brandSizeGroups[key].size > 1 && item.packType) {
          // Add pack type in brackets: P for Pieces, G for Grams
          const packTypeIndicator = item.packType === 'P' ? 'P' : 'G';
          displaySizeCode = `${item.sizeCode}(${packTypeIndicator})`;
        }
        
        return `
        <tr>
          <td class="center">${item.serialNo}</td>
          <td class="brand-name">${item.brandName}(${item.brandNumber})</td>
          <td class="center">${displaySizeCode}</td>
          <td class="number">${item.openingStock}</td>
          <td class="number">${item.receivedStock}</td>
          <td class="number">${item.totalStock}</td>
          <td class="number">${item.closingStock}</td>
          <td class="number">${item.sales}</td>
          <td class="number">${Math.round(item.price)}</td>
          <td class="number">${formatCurrency(item.salesValue || 0)}</td>
        </tr>
      `;
      }).join('');
    };

    const generateTotalRow = (data) => {
      return `
        <tr class="bottles-totals-row">
          <td colspan="3">TOTAL BOTTLES</td>
          <td class="number red-text">${data.reduce((sum, item) => sum + (item.openingStock || 0), 0)}</td>
          <td class="number red-text">${data.reduce((sum, item) => sum + (item.receivedStock || 0), 0)}</td>
          <td class="number red-text">${data.reduce((sum, item) => sum + (item.totalStock || 0), 0)}</td>
          <td class="number red-text">${data.reduce((sum, item) => sum + (item.closingStock || 0), 0)}</td>
          <td class="number red-text">${data.reduce((sum, item) => sum + (item.sales || 0), 0)}</td>
          <td></td>
          <td class="number red-text">${formatCurrency(data.reduce((sum, item) => sum + (item.salesValue || 0), 0))}</td>
        </tr>
      `;
    };

    // Generate accessories table
    const accessoriesItems = state.accessorySalesData?.items || [];
    const generateAccessoriesTable = () => {
      if (accessoriesItems.length === 0) return '';
      
      // Process accessories data for display
      const processedAccessories = accessoriesItems.map((item, index) => {
        const openingStock = item.opening_stock || 0;
        const receivedStock = item.received_stock || 0;
        const totalStock = openingStock + receivedStock;
        const closingStock = item.closing_stock !== null ? item.closing_stock : totalStock;
        const sales = Math.max(0, totalStock - closingStock);
        const price = parseFloat(item.price_per_unit) || parseFloat(item.mrp) || 0;
        const salesValue = sales * price;
        
        return {
          serialNo: index + 1,
          name: item.name,
          size: item.size || '-',
          openingStock,
          receivedStock,
          totalStock,
          closingStock,
          sales,
          price,
          salesValue
        };
      });
      
      const accessoryTotals = {
        openingStock: processedAccessories.reduce((sum, item) => sum + item.openingStock, 0),
        receivedStock: processedAccessories.reduce((sum, item) => sum + item.receivedStock, 0),
        totalStock: processedAccessories.reduce((sum, item) => sum + item.totalStock, 0),
        closingStock: processedAccessories.reduce((sum, item) => sum + item.closingStock, 0),
        sales: processedAccessories.reduce((sum, item) => sum + item.sales, 0),
        salesValue: processedAccessories.reduce((sum, item) => sum + item.salesValue, 0)
      };
      
      return `
        <div class="accessories-section" style="margin-top: 20px;">
          <h3 style="margin-bottom: 10px; color: #059669;">ACCESSORIES SALE:</h3>
          <table class="single-table" style="border: 2px solid #059669;">
            <thead style="background-color: #ecfdf5;">
              <tr>
                <th>SNO</th>
                <th>NAME</th>
                <th>SIZE</th>
                <th>O.S</th>
                <th>REC</th>
                <th>TTL</th>
                <th>C.S</th>
                <th>SALE</th>
                <th>PRICE</th>
                <th>AMOUNT</th>
              </tr>
            </thead>
            <tbody>
              ${processedAccessories.map(item => `
                <tr>
                  <td class="center">${item.serialNo}</td>
                  <td class="brand-name">${item.name}</td>
                  <td class="center">${item.size}</td>
                  <td class="number">${item.openingStock}</td>
                  <td class="number">${item.receivedStock}</td>
                  <td class="number">${item.totalStock}</td>
                  <td class="number">${item.closingStock}</td>
                  <td class="number">${item.sales}</td>
                  <td class="number">${Math.round(item.price)}</td>
                  <td class="number">${formatCurrency(item.salesValue)}</td>
                </tr>
              `).join('')}
              <tr class="bottles-totals-row" style="background-color: #d1fae5; border-top: 2px solid #059669;">
                <td colspan="3" style="color: #065f46;"><strong>TOTAL ACCESSORIES</strong></td>
                <td class="number" style="color: #065f46;"><strong>${accessoryTotals.openingStock}</strong></td>
                <td class="number" style="color: #065f46;"><strong>${accessoryTotals.receivedStock}</strong></td>
                <td class="number" style="color: #065f46;"><strong>${accessoryTotals.totalStock}</strong></td>
                <td class="number" style="color: #065f46;"><strong>${accessoryTotals.closingStock}</strong></td>
                <td class="number" style="color: #065f46;"><strong>${accessoryTotals.sales}</strong></td>
                <td></td>
                <td class="number" style="color: #065f46;"><strong>${formatCurrency(accessoryTotals.salesValue)}</strong></td>
              </tr>
            </tbody>
          </table>
        </div>
      `;
    };

    let tableContent = '';
    
    if (twoTableFormat && state.stockData.length > BUSINESS_CONFIG.TABLE_SPLIT_THRESHOLD) {
      const splitIndex = Math.ceil(state.stockData.length / 2);
      const leftData = state.stockData.slice(0, splitIndex);
      const rightData = state.stockData.slice(splitIndex);
      
      tableContent = `
        <div class="two-table-wrapper">
          <div class="left-column">
            <div class="column-header">${shopName}</div>
            <div class="column-table">
              <table>
                <thead>
                  <tr>
                    <th>SNO</th><th>NAME</th><th>Size Code</th><th>O.S</th><th>REC</th>
                    <th>TTL</th><th>C.S</th><th>SALE</th><th>PRICE</th><th>AMOUNT</th>
                  </tr>
                </thead>
                <tbody>
                  ${generateTableRows(leftData)}
                  ${generateTotalRow(leftData)}
                </tbody>
              </table>
            </div>
          </div>
          <div class="right-column">
            <div class="column-header">${startDate === endDate ? formatDate(startDate) : formatDate(startDate) + ' To ' + formatDate(endDate)}</div>
            <div class="column-table">
              <table>
                <thead>
                  <tr>
                    <th>SNO</th><th>NAME</th><th>Size Code</th><th>O.S</th><th>REC</th>
                    <th>TTL</th><th>C.S</th><th>SALE</th><th>PRICE</th><th>AMOUNT</th>
                  </tr>
                </thead>
                <tbody>
                  ${generateTableRows(rightData)}
                  ${generateTotalRow(rightData)}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      `;
    } else {
      tableContent = `
        <table class="single-table">
          <thead>
            <tr>
              <th>SNO</th><th>NAME</th><th>Size Code</th><th>O.S</th><th>REC</th>
              <th>TTL</th><th>C.S</th><th>SALE</th><th>PRICE</th><th>AMOUNT</th>
            </tr>
          </thead>
          <tbody>
            ${generateTableRows(state.stockData)}
            <tr class="bottles-totals-row">
              <td colspan="3">TOTAL BOTTLES</td>
              <td class="number red-text">${state.stockData.reduce((sum, item) => sum + (item.openingStock || 0), 0)}</td>
              <td class="number red-text">${state.stockData.reduce((sum, item) => sum + (item.receivedStock || 0), 0)}</td>
              <td class="number red-text">${state.stockData.reduce((sum, item) => sum + (item.totalStock || 0), 0)}</td>
              <td class="number red-text">${state.stockData.reduce((sum, item) => sum + (item.closingStock || 0), 0)}</td>
              <td class="number red-text">${state.stockData.reduce((sum, item) => sum + (item.sales || 0), 0)}</td>
              <td></td>
              <td class="number red-text">${formatCurrency(totalSales)}</td>
            </tr>
          </tbody>
        </table>
      `;
    }

    return `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no">
      <title>Sale Sheet - ${shopName}</title>
      <style>
        /* Print styles - critical for iOS */
        @page { 
          margin: 10mm; 
          size: A4 portrait; 
        }
        
        @media print {
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          * { -webkit-print-color-adjust: exact !important; }
          .action-buttons { display: none !important; }
        }
        
        /* Base styles */
        * { box-sizing: border-box; }
        body { 
          font-family: Arial, sans-serif; 
          font-size: ${twoTableFormat && isMobileOrTablet ? '10px' : '11px'}; 
          margin: 0; 
          padding: ${isMobileOrTablet ? '4px' : '8px'};
          padding-top: ${isMobileOrTablet ? '50px' : '70px'};
          -webkit-font-smoothing: antialiased;
        }
        
        /* Action Buttons - Shadcn Inspired */
        .action-buttons {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          background: white;
          padding: 14px;
          box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
          display: flex;
          gap: 12px;
          justify-content: center;
          align-items: center;
          z-index: 1000;
          border-bottom: 1px solid #e5e5e5;
        }
        
        /* Shadcn Outline Button Style */
        .action-btn {
          /* Layout */
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          
          /* Shadcn outline variant */
          background-color: #ffffff;
          border: 2px solid #000000;
          color: #09090b;
          
          /* Shadcn sizing */
          padding: 0.625rem 1.25rem;
          font-size: 14px;
          font-weight: 500;
          line-height: 1.5;
          letter-spacing: -0.01em;
          
          /* Shadcn styling */
          border-radius: 0.375rem;
          box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
          
          /* Shadcn transitions */
          transition: all 150ms cubic-bezier(0.4, 0, 0.2, 1);
          
          /* Touch optimization */
          cursor: pointer;
          user-select: none;
          -webkit-user-select: none;
          -webkit-tap-highlight-color: transparent;
          touch-action: manipulation;
        }
        
        .action-btn:hover {
          background-color: #18181b;
          color: #ffffff;
          border-color: #18181b;
          box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.1);
          transform: translateY(-1px);
        }
        
        .action-btn:active {
          transform: translateY(0);
          box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
        }
        
        .btn-icon {
          font-size: 16px;
          font-weight: bold;
          line-height: 1;
        }
        
        /* Mobile styles */
        @media (max-width: 768px) {
          .action-btn {
            padding: 0.5rem 1rem;
            font-size: 13px;
            min-height: 44px;
          }
          .btn-icon {
            font-size: 15px;
          }
        }
        
        /* Center buttons */
        @media (min-width: 769px) {
          .action-buttons {
            justify-content: center;
          }
        }
        
        .content-wrapper {
          max-width: 1200px;
          margin: 0 auto;
        }
        
        .sale-sheet-title { text-align: center; font-size: 18px; font-weight: bold; margin-bottom: 15px; }
        .header { display: flex; justify-content: space-between; margin-bottom: 10px; border-bottom: 1px solid #000; }
        .shop-info { font-size: 16px; font-weight: bold; }
        .date-range { font-size: 16px; font-weight: bold; }
        
        /* Table styles */
        table { 
          width: 100%; 
          border-collapse: collapse; 
          border: 1px solid #888;
          page-break-inside: auto;
        }
        tr { page-break-inside: avoid; page-break-after: auto; }
        thead { display: table-header-group; }
        tfoot { display: table-footer-group; }
        th, td { 
          border: 1px solid #888; 
          padding: 3px; 
          text-align: center;
          vertical-align: middle;
          word-wrap: break-word;
        }
        th { font-size: 12px; }
        .center { text-align: center; vertical-align: middle; }
        .number { text-align: center; vertical-align: middle; font-size: 13px; }
        .brand-name { text-align: left; vertical-align: middle; max-width: 150px; word-wrap: break-word; white-space: normal; font-size: 12px; }
        
        /* Single table specific: center aligned except brand names */
        .single-table td { text-align: center; vertical-align: middle; }
        .single-table td.brand-name { text-align: left; }
        .bottles-totals-row { font-weight: bold; border-top: 1px solid #888; }
        .red-text { color: #ff0000; }
        
        /* Two-table layout */
        .two-table-wrapper {
          display: table;
          border: 1px solid #888;
          width: 100%;
          border-collapse: collapse;
        }
        .two-table-wrapper .left-column,
        .two-table-wrapper .right-column {
          display: table-cell;
          width: 50%;
          vertical-align: top;
          border: 1px solid #888;
        }
        .two-table-wrapper .column-header {
          font-size: 16px;
          font-weight: bold;
          padding: 4px;
          border-bottom: 1px solid #888;
        }
        .two-table-wrapper .left-column .column-header { text-align: left; }
        .two-table-wrapper .right-column .column-header { text-align: right; }
        .two-table-wrapper .column-table {
          padding: 2px;
        }
        .two-table-wrapper .column-table table {
          border: 1px solid #888;
        }
        .two-table-wrapper .brand-name { max-width: 125px; }
        .two-table-wrapper .bottles-totals-row { border-top: 1px solid #888; }
        
        /* Adjust padding for mobile/tablet */
        ${isMobileOrTablet ? `
          th, td { padding: ${window.innerWidth <= 768 ? '1px' : '2px 1px'}; }
          .header-cell { padding: 2px 1px; }
        ` : ''}
        
        /* Summary sections */
        .summary { margin-top: 15px; page-break-inside: avoid; }
        .summary-table { 
          width: 100%; 
          border-collapse: collapse; 
          margin: 10px 0; 
          border: 1px solid #888;
          page-break-inside: avoid;
        }
        .income-expenses-section { 
          margin-top: 20px;
          page-break-inside: avoid;
        }
        .income-table, .expenses-table { 
          width: 100%; 
          border-collapse: collapse; 
          border: 1px solid #888; 
          margin: 10px 0;
          page-break-inside: avoid;
        }
      </style>
    </head>
    <body>
      <!-- Action Buttons -->
      <div class="action-buttons">
        <button class="action-btn download-btn" onclick="handleDownload()">
          <span class="btn-icon">⬇</span>
          <span>Download PDF</span>
        </button>
      </div>
      
      <div class="content-wrapper">
        <div class="sale-sheet-title">Sale Sheet</div>
        ${!twoTableFormat ? `<div class="header">
          <div class="shop-info">${shopName}</div>
          <div class="date-range">${startDate === endDate ? formatDate(startDate) : formatDate(startDate) + ' To ' + formatDate(endDate)}</div>
        </div>` : ''}

      ${tableContent}

      ${generateAccessoriesTable()}

      <div class="summary">
        <h3>SUMMARY:</h3>
        <table class="summary-table">
          <thead>
            <tr><th>S.No</th><th>PARTICULARS</th><th>AMOUNT</th></tr>
          </thead>
          <tbody>
            <tr><td>1</td><td>OPENING STOCK VALUE</td><td>${formatCurrency(openingStockValue)}</td></tr>
            <tr><td>2</td><td>RECEIVED STOCK VALUE</td><td>${formatCurrency(receivedStockValue)}</td></tr>
            <tr><td>3</td><td>CLOSING STOCK VALUE</td><td>${formatCurrency(closingStockValue)}</td></tr>
            <tr><td>4</td><td>TOTAL SALE</td><td>${formatCurrency(totalSales)}</td></tr>
            <tr><td>5</td><td>OTHER INCOME</td><td>${formatCurrency(totalIncome)}</td></tr>
            <tr><td>6</td><td>OPENING COUNTER BALANCE</td><td>${formatCurrency(openingBalance)}</td></tr>
            <tr><td>7</td><td>CASH</td><td>${formatCurrency(cash)}</td></tr>
            <tr><td>8</td><td>CARD</td><td>${formatCurrency(card)}</td></tr>
            <tr><td>9</td><td>UPI</td><td>${formatCurrency(upi)}</td></tr>
            <tr><td>10</td><td>EXPENSES</td><td>${formatCurrency(totalExpenses)}</td></tr>
            <tr><td>11</td><td>CLOSING COUNTER BALANCE</td><td>${formatCurrency(closingBalance)}</td></tr>
          </tbody>
        </table>
      </div>

      <div class="income-expenses-section">
        <div>
          <h3>OTHER INCOME:</h3>
          <table class="income-table">
            <thead>
              <tr>
                <th>S.No</th>
                <th>SOURCE</th>
                ${startDate === endDate ? '<th>DESCRIPTION</th>' : ''}
                <th>AMOUNT</th>
              </tr>
            </thead>
            <tbody>
              ${combinedIncomeArray.map((item, index) => 
                `<tr${item.source === 'Accessories Sale' ? ' style="background-color: #f0f9ff;"' : ''}>
                  <td>${index + 1}</td>
                  <td>${item.source || 'N/A'}</td>
                  ${startDate === endDate ? `<td>${item.description || '-'}</td>` : ''}
                  <td>${formatCurrency(item.amount || 0)}</td>
                </tr>`
              ).join('')}
              ${combinedIncomeArray.length === 0 ? `<tr><td colspan="${startDate === endDate ? '4' : '3'}">No income records</td></tr>` : ''}
              <tr class="bottles-totals-row">
                <td colspan="${startDate === endDate ? '3' : '2'}"><strong>TOTAL</strong></td>
                <td><strong>${formatCurrency(totalIncome)}</strong></td>
              </tr>
            </tbody>
          </table>
        </div>
        
        <div>
          <h3>EXPENSES:</h3>
          <table class="expenses-table">
            <thead>
              <tr>
                <th>S.No</th>
                <th>CATEGORY</th>
                ${startDate === endDate ? '<th>DESCRIPTION</th>' : ''}
                <th>AMOUNT</th>
              </tr>
            </thead>
            <tbody>
              ${(state.expensesData || []).map((item, index) => 
                `<tr>
                  <td>${index + 1}</td>
                  <td>${item.category || 'N/A'}</td>
                  ${startDate === endDate ? `<td>${item.description || '-'}</td>` : ''}
                  <td>${formatCurrency(item.amount || 0)}</td>
                </tr>`
              ).join('')}
              ${(state.expensesData || []).length === 0 ? `<tr><td colspan="${startDate === endDate ? '4' : '3'}">No expense records</td></tr>` : ''}
              <tr class="bottles-totals-row">
                <td colspan="${startDate === endDate ? '3' : '2'}"><strong>TOTAL</strong></td>
                <td><strong>${formatCurrency(totalExpenses)}</strong></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      </div>
      
      <script>
        // Download as PDF functionality
        function handleDownload() {
          // Use browser's print to PDF feature
          window.print();
        }
        
      </script>
    </body>
    </html>
    `;
  };

  // Render loading state
  if (state.fetchState === FETCH_STATES.LOADING) {
    return (
      <div className="container mx-auto px-4 py-8">
        <LoadingDisplay progress={state.loadingProgress} />
      </div>
    );
  }

  // Render error state
  if (state.fetchState === FETCH_STATES.ERROR) {
    return (
      <div className="container mx-auto px-4 py-8">
        <ErrorDisplay error={state.error} onRetry={fetchAllData} />
      </div>
    );
  }

  const totalBrands = calculations.totalProducts;
  const showBothFormats = calculations.showBothFormats;

  return (
      <main className="container mx-auto px-4 py-8 max-w-7xl">
        <div className="mb-8 space-y-2 text-left">
          <h1 className="text-4xl font-bold text-left">Download Sale Sheet</h1>
          <p className="text-base text-muted-foreground text-left">Generate PDF or Excel sale sheet with financial summary</p>
        </div>

        <DateRangeSelector
          reportType={dateSelector.reportType}
          onReportTypeChange={dateSelector.setReportType}
          selectedDateObj={dateSelector.selectedDateObj}
          onDateSelect={dateSelector.handleDateSelect}
          selectedYear={dateSelector.selectedYear}
          onYearChange={dateSelector.handleYearChange}
          selectedMonth={dateSelector.selectedMonth}
          onMonthChange={dateSelector.handleMonthChange}
          selectedWeek={dateSelector.selectedWeek}
          onWeekChange={dateSelector.handleWeekChange}
          weeksInMonth={dateSelector.weeksInMonth}
          customStartDateObj={dateSelector.customStartDateObj}
          customEndDateObj={dateSelector.customEndDateObj}
          onCustomRangeSelect={dateSelector.handleCustomRangeSelect}
          onGenerateReport={fetchAllData}
          loading={state.fetchState === FETCH_STATES.LOADING}
          getMaxDate={dateSelector.getCurrentBusinessDateObject}
          hideGenerateButton={true}
          excludeReportTypes={['yearly']}
        />

        <div className="flex flex-col items-center gap-3 mt-6 mb-6">
          <div className="flex flex-wrap justify-center gap-4">
            {showBothFormats ? (
              <>
                <Button 
                  size="lg"
                  onClick={generateSingleTablePDF}
                  disabled={state.generating}
                >
                  {state.generatingType === 'pdf' ? (
                    <>
                      <Spinner className="mr-2 h-4 w-4" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <FileDown className="mr-2 h-4 w-4" />
                      Download Single Table PDF
                    </>
                  )}
                </Button>
                <Button 
                  size="lg"
                  onClick={generateTwoTablePDF}
                  disabled={state.generating}
                >
                  {state.generatingType === 'pdf' ? (
                    <>
                      <Spinner className="mr-2 h-4 w-4" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <FileDown className="mr-2 h-4 w-4" />
                      Download Two-Table PDF
                    </>
                  )}
                </Button>
              </>
            ) : (
              <Button 
                size="lg"
                onClick={generateSingleTablePDF}
                disabled={state.generating}
              >
                {state.generatingType === 'pdf' ? (
                  <>
                    <Spinner className="mr-2 h-4 w-4" />
                    Generating...
                  </>
                ) : (
                  <>
                    <FileDown className="mr-2 h-4 w-4" />
                    Download PDF
                  </>
                )}
              </Button>
            )}
            <Button 
              size="lg"
              variant="outline"
              onClick={generateExcelFile}
              disabled={state.generating}
              className="border-green-600 text-green-700 hover:bg-green-50"
            >
              {state.generatingType === 'excel' ? (
                <>
                  <Spinner className="mr-2 h-4 w-4" />
                  Generating...
                </>
              ) : (
                <>
                  <FileSpreadsheet className="mr-2 h-4 w-4" />
                  Download Excel
                </>
              )}
            </Button>
          </div>
          
          {!state.closingStockStatus?.isFullySaved && (
            <Alert className="max-w-2xl mx-auto">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                {state.closingStockStatus?.isPartiallySaved 
                  ? `Closing stock incomplete: ${state.closingStockStatus.savedProducts}/${state.closingStockStatus.totalProducts} products saved. PDF will show remaining items with Total Stock as Closing Stock.`
                  : 'Closing stock not saved yet. PDF will show Total Stock (Opening + Received) as Closing Stock with zero sales.'}
              </AlertDescription>
            </Alert>
          )}
        </div>

        <div className="grid md:grid-cols-3 gap-6 mb-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Products</CardTitle>
              <Package className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{totalBrands}</div>
              <p className="text-xs text-muted-foreground mt-1">
                Products in inventory
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Sales</CardTitle>
              <IndianRupee className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">₹{calculations.totalSales.toLocaleString('en-IN')}</div>
              <p className="text-xs text-muted-foreground mt-1">
                Revenue generated
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Date Range</CardTitle>
              <Calendar className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{getDateRange()}</div>
              <p className="text-xs text-muted-foreground mt-1">
                Report period
              </p>
            </CardContent>
          </Card>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <Card className="shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Report Contents
              </CardTitle>
              <CardDescription className="text-left">What's included in your sale sheet</CardDescription>
            </CardHeader>
            <CardContent className="text-left">
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Complete stock movement (Opening, Received, Total, Closing, Sales)</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Sales values and pricing information</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Financial summary with opening/closing balances</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Payment collections (Cash, UPI, Card)</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Income and expense details</span>
                </li>
              </ul>
            </CardContent>
          </Card>
          
          <Card className="shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CalendarCheck className="h-5 w-5" />
                Date Logic
              </CardTitle>
              <CardDescription className="text-left">How dates are calculated</CardDescription>
            </CardHeader>
            <CardContent className="text-left">
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="h-5 w-5 text-blue-500 mt-0.5 flex-shrink-0" />
                  <span><strong>Single Date:</strong> Shows complete stock data for selected date</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="h-5 w-5 text-blue-500 mt-0.5 flex-shrink-0" />
                  <span><strong>Date Range:</strong> Opening stock from start date, closing stock from end date</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="h-5 w-5 text-blue-500 mt-0.5 flex-shrink-0" />
                  <span>Business day starts at 11:30 AM IST</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="h-5 w-5 text-blue-500 mt-0.5 flex-shrink-0" />
                  <span>All financial data uses the end date for calculations</span>
                </li>
              </ul>
            </CardContent>
          </Card>
        </div>
    </main>
  );
}

export default DownloadSaleSheet;
